import {
  endedArray,
  extraTimeArray,
  hasChronoArray,
  liveArray,
  notEndedArray,
  regularTimeArray,
  shootoutArray,
  MatchPhase,
  MatchPhaseRound,
  MatchStatusType,
  ABANDONED,
  CANCELLED,
  EXTRAFIRSTHALF,
  EXTRAHALFTIME,
  EXTRASECONDHALF,
  FIRST_LEG,
  FIRSTHALF,
  FULLTIME,
  FULLTIME90,
  FULLTIMEAFTEROVERTIME,
  FULLTIMEAFTERSHOOTOUT,
  FULLTIMEPENS,
  HALFTIME,
  LIVE,
  POSTPONED,
  PREMATCH,
  SECONDHALF,
  SECOND_LEG,
  SHOOTOUT,
  FINAL,
} from '../typings/match';
import t from '../labels/labelling-utils';
import { IMatchRow } from '../components/molecules/match-row/match-row';
import { ITeam } from '../components/molecules/team/team';
import { IMatchDayList } from '../components/organisms/results-table/results-table';
import { ICompetitionCalendarResultsTable } from '../components/templates/competition-calendar-results/competition-calendar-results';
import { ICompetitionResultsTable } from '../components/templates/match-results/match-results';

import { dateFormat } from './times';
import { compareNumberAndNumericString } from './numbers';

/* Validators - Match status */
export const isAbandoned = (status: MatchStatusType): boolean => status === ABANDONED;
export const isCancelled = (status: MatchStatusType): boolean => status === CANCELLED;
export const isPostponed = (status: MatchStatusType): boolean => status === POSTPONED;
export const isNotEnded = (status: MatchStatusType): boolean => notEndedArray.includes(status);
export const isPreMatch = (status: MatchStatusType): boolean => status === PREMATCH;
export const isEnded = (status: MatchStatusType): boolean => endedArray.includes(status);
export const isEndedAfterOverTime = (status: MatchStatusType): boolean => status === FULLTIMEAFTEROVERTIME;
export const isEndedAfterShootout = (status: MatchStatusType): boolean => status === FULLTIMEAFTERSHOOTOUT;
export const isLive = (status: MatchStatusType): boolean => liveArray.includes(status);
export const isInRegularTime = (status: MatchStatusType) => isLive(status) && regularTimeArray.includes(status);
export const isInExtraTime = (status: MatchStatusType) => isLive(status) && extraTimeArray.includes(status);
export const isInShootoutTime = (status: MatchStatusType) => isLive(status) && shootoutArray.includes(status);
export const hasChrono = (status: MatchStatusType): boolean => hasChronoArray.includes(status);

/* Validators - Match phase/round */
export const isFirstLeg = (phase: MatchPhase, phaseRound: MatchPhaseRound): boolean =>
  typeof phase !== 'undefined' && phase !== null && phaseRound === FIRST_LEG;
export const isSecondLeg = (phase: MatchPhase, phaseRound: MatchPhaseRound): boolean =>
  typeof phase !== 'undefined' && phase !== null && phaseRound === SECOND_LEG;
export const isKnockout = (phase: MatchPhase, phaseRound: MatchPhaseRound): boolean =>
  typeof phase !== 'undefined' && phase !== null && (typeof phaseRound === 'undefined' || phaseRound === null);
export const isFinal = (phase: MatchPhase, phaseRound: MatchPhaseRound): boolean =>
  isKnockout(phase, phaseRound) && phase === FINAL;

/* LIVE status labels utils*/
const liveMatchTimeLabelMap = {
  [LIVE]: t('match.time.live'),
  [FIRSTHALF]: t('match.time.first.half'),
  [HALFTIME]: t('match.time.halftime'),
  [SECONDHALF]: t('match.time.second.half'),
  [FULLTIME90]: t('match.time.fulltime.extra'),
  [EXTRAHALFTIME]: t('match.time.extra.halftime'),
  [EXTRAFIRSTHALF]: t('match.time.extra.first.half'),
  [EXTRASECONDHALF]: t('match.time.extra.second.half'),
  [FULLTIMEPENS]: t('match.time.fulltime.shootout'),
  [SHOOTOUT]: t('match.time.shootout'),
  [POSTPONED]: t('match.status.postponed'),
  [ABANDONED]: t('match.status.abandoned'),
  [CANCELLED]: t('match.status.cancelled'),
};

export const getLiveMatchTimeLabel = (matchStatus: MatchStatusType): string | null =>
  liveMatchTimeLabelMap[matchStatus] || null;

/* ALL statuses labels utils */
const mapMatchStatusLabel = {
  [LIVE]: t('match.time.live'),
  [FIRSTHALF]: t('match.time.first.half'),
  [HALFTIME]: t('match.time.halftime'),
  [SECONDHALF]: t('match.time.second.half'),
  [FULLTIME]: t('match.time.fulltime'),
  [FULLTIME90]: t('match.time.fulltime.extra'),
  [EXTRAFIRSTHALF]: t('match.time.extra.first.half'),
  [EXTRAHALFTIME]: t('match.time.extra.halftime'),
  [EXTRASECONDHALF]: t('match.time.extra.second.half'),
  [FULLTIMEAFTEROVERTIME]: t('match.time.fulltime'),
  [FULLTIMEPENS]: t('match.time.fulltime.shootout'),
  [SHOOTOUT]: t('match.time.shootout'),
  [FULLTIMEAFTERSHOOTOUT]: t('match.time.fulltime'),
  [POSTPONED]: t('match.status.postponed'),
  [ABANDONED]: t('match.status.abandoned'),
  [CANCELLED]: t('match.status.cancelled'),
};

export const getMatchStatusLabel = (matchStatus: MatchStatusType): string | undefined =>
  mapMatchStatusLabel[matchStatus] || undefined;

// DTR/DTS/DCR
export type MatchEndedLabel = 'DTR' | 'DTS' | 'DCR' | 'TR' | 'TS' | 'CR';

export const getMatchEndedLabel = (match: IMatchRow): MatchEndedLabel => {
  const { home, away, status } = match;

  // All GOALS are OPTIONAL in IResult interface
  // I use DESTRUCTURING with both RENAMING and ASSIGNING DEFAULT VALUES
  const {
    /* istanbul ignore next: default value */ overtimeGoal: homeOvertimeGoal = 0,
    /* istanbul ignore next: default value */ shootoutGoal: homeShootoutGoal = 0,
  } = home;
  const {
    /* istanbul ignore next: default value */ overtimeGoal: awayOvertimeGoal = 0,
    /* istanbul ignore next: default value */ shootoutGoal: awayShootoutGoal = 0,
  } = away;

  // LC 2020-12-02 - New UX
  if (isInExtraTime(status)) {
    return 'TS';
  }
  if (isInShootoutTime(status)) {
    return 'CR';
  }

  // TODO: LC 2020-12-09 - NEW FULLTIME STATUES (will be returned by FE QUERY TRANSFOMER, not BE DATA)
  if (isEndedAfterOverTime(status)) {
    return 'DTS';
  }
  if (isEndedAfterShootout(status)) {
    return 'DCR';
  }

  // SHOOTOUT - If there are SHOOTOUT GOALS then: match is LIVE or ENDED?
  if (homeShootoutGoal > 0 || awayShootoutGoal > 0) {
    return isLive(status) ? 'DTS' : 'DCR';
  }

  // OVER TIME - If there are OVERTIME GOALS then: match is LIVE or ENDED?
  if (homeOvertimeGoal > 0 || awayOvertimeGoal > 0) {
    return isLive(status) ? 'DTR' : 'DTS';
  }

  // DTR - Match is LIVE or ENDED?
  return isEnded(status) ? 'DTR' : 'TR';
};

// ISS-4085 - competitionPhaseWinnerTeamId = NUMBER, team.id = STRING
export interface IWinnerLoserTeams {
  winner: ITeam | null;
  loser: ITeam | null;
}

export const addIsLoseOnTeams = (match: IMatchRow): IMatchRow => ({
  ...match,
  home: {
    ...match.home,
    isLose: compareNumberAndNumericString(match.away.id, match.competitionPhaseWinnerTeamId),
  },
  away: {
    ...match.away,
    isLose: compareNumberAndNumericString(match.home.id, match.competitionPhaseWinnerTeamId),
  },
});

export const getWinnerLoserTeams = (match: IMatchRow): IWinnerLoserTeams => {
  const { home, away, status, competitionPhaseWinnerTeamId } = match;

  // Show WINNER/LOSER only if MATCH is ENDED
  if (!isEnded(status)) {
    return {
      winner: null,
      loser: null,
    };
  }

  // Show HEADER both if AGGREGATE RESULT is EVEN or if MATCH is ENDED in OVERTIME/SHOOTOUTS
  const { /* istanbul ignore next: default value */ totalGoalLegs: homeTotalGoalLegs = 0 } = home;
  const { /* istanbul ignore next: default value */ totalGoalLegs: awayTotalGoalLegs = 0 } = away;
  const isEven: boolean = homeTotalGoalLegs === awayTotalGoalLegs && status === FULLTIME;
  const isEndedAfter: boolean = isEndedAfterOverTime(status) || isEndedAfterShootout(status);

  // Match NUMBER and STRING data types
  if (home.id && `${competitionPhaseWinnerTeamId}` === `${home.id}`) {
    // away.isLose = true; // FIXME: Original object mutation, to avoid refactoring MatchRow and other components to pass-throug isLose prop

    return isEven || isEndedAfter // FIXME: only if MATCH is ENDED in OVERTIME/SHOOTOUTS!
      ? {
          winner: home,
          loser: away,
        }
      : {
          winner: null,
          loser: null,
        };
  } else if (away.id && `${competitionPhaseWinnerTeamId}` === `${away.id}`) {
    // home.isLose = true; // FIXME: Original object mutation, to avoid refactoring MatchRow and other components to pass-throug isLose prop

    return isEven || isEndedAfter // FIXME: only if MATCH is ENDED in OVERTIME/SHOOTOUTS!
      ? {
          winner: away,
          loser: home,
        }
      : {
          winner: null,
          loser: null,
        };
  } else {
    // To avoid returning FALSE POSITIVE if BE sends a wrong competitionPhaseWinnerTeamId (different from both HOME and AWAY team id)
    return {
      winner: null,
      loser: null,
    };
  }
};

// Actually unused, but can be useful so I leave it here
export const getWinnerTeam = (match: IMatchRow): string | null => {
  const { home, away, competitionPhaseWinnerTeamId } = match;
  const winner: ITeam[] = [home, away].filter((team) => `${team.id}` === `${competitionPhaseWinnerTeamId}`);

  return winner.length > 0 ? winner[0].name : null;
};

export interface IMatchResult {
  status: MatchStatusType;
  text: string;
}

export const getResult = (
  status: MatchStatusType = 'PreMatch',
  date: string = '',
  home: ITeam,
  away: ITeam,
  showLiveResult: boolean = false
): IMatchResult => {
  const result: IMatchResult = {
    status,
    text: '',
  };
  const {
    /* istanbul ignore next: default value */ goal: homeGoal = 0,
    /* istanbul ignore next: default value */ shootoutGoal: homeShootoutGoal = 0,
  } = home;
  const {
    /* istanbul ignore next: default value */ goal: awayGoal = 0,
    /* istanbul ignore next: default value */ shootoutGoal: awayShootoutGoal = 0,
  } = away;

  if (isPostponed(status) || isCancelled(status)) {
    result.text = '-';
  } else if (isLive(status) && !showLiveResult) {
    result.text = t('match.status.live');
  } else if (isPreMatch(status)) {
    result.text = dateFormat({ date, format: 'HH:mm' });
  } else {
    result.text = isInShootoutTime(status)
      ? [homeShootoutGoal, awayShootoutGoal].join(' - ') // During SHOOTOUTS we show shootouts only in RESULT
      : [homeGoal, awayGoal].join(' - ');
  }

  return result;
};

export const getMatchResult = (match: IMatchRow, showLiveResult: boolean): IMatchResult => {
  const { date, home, away, status } = match;

  return getResult(status, date, home, away, showLiveResult);
};

// How to tell if COMPETITION is LINEAR or NON LINEAR?
export const isNonLinearCompetition = (
  competitionMatchList: Array<ICompetitionCalendarResultsTable | ICompetitionResultsTable>
): boolean => {
  // If competitionPhase <> NULL (e.g. "Ottavi") then COMPETITION is NON LINEAR
  const filter = competitionMatchList.filter((item) => item.competitionPhase && item.competitionPhase !== null);
  if (filter.length > 0) {
    return true;
  }

  const matchDayList: IMatchDayList[] = competitionMatchList
    .map((competitionMatch) => competitionMatch.matchDayList)
    .reduce((acc, _matchDayList) => acc.concat(_matchDayList), []);

  return isNonLinearCompetitionMatchList(matchDayList);
};

export const isNonLinearCompetitionMatchList = (matchDayList: IMatchDayList[]): boolean => {
  const competitionPhases = matchDayList
    .map((matchDay) => matchDay.matchList)
    .reduce((acc, matchList) => acc.concat(matchList), [])
    .map((matchListRow) => ({
      // match: `${matchListRow?.home.name}-${matchListRow?.away.name}`, // Useful for DEBUG only!
      competitionPhase: matchListRow?.competitionPhase || null, // TODO: does not exist in REAL BE data but only in our generateMatchList() mocked data!
      competitionPhaseRound: matchListRow?.competitionPhaseRound || null,
      roundGroup: matchListRow?.roundGroup || null,
    }));

  const filter = competitionPhases.filter((item) => item.competitionPhase !== null || item.roundGroup !== null);
  return filter.length > 0;
};

// export type matchState = 'prematch' | 'live' | 'ended' | 'postponed' | 'abandoned' | 'cancelled';
export const matchStates: { [key: string]: any } = {
  prematch: isPreMatch,
  live: isLive,
  ended: isEnded,
  postponed: isPostponed,
  abandoned: isAbandoned,
  cancelled: isCancelled,
};

export const getMatchState = (status: MatchStatusType): string =>
  Object.keys(matchStates)
    .map((state) => {
      const valid = matchStates[state];
      if (valid(status)) {
        return state;
      }
      return false;
    })
    .filter(Boolean)
    .join('');

// FIXME: LC 2021-02-17 - Get new BE MatchStatusType "FullTimeAfterOverTime" and "FullTimeAfterShootOut" (until will be provided by BE)
export const computeFullTime = (match: IMatchRow): IMatchRow => {
  if (
    match.status === FULLTIME &&
    ((match.home.shootoutGoal && match.home.shootoutGoal > 0) ||
      (match.away.shootoutGoal && match.away.shootoutGoal > 0))
  ) {
    return {
      ...match,
      status: FULLTIMEAFTERSHOOTOUT,
    };
  } else if (
    match.status === FULLTIME &&
    ((match.home.overtimeGoal && match.home.overtimeGoal > 0) ||
      (match.away.overtimeGoal && match.away.overtimeGoal > 0))
  ) {
    return {
      ...match,
      status: FULLTIMEAFTEROVERTIME,
    };
  } else {
    return match;
  }
};
